import { useCallback, useEffect, useState, useMemo } from 'react';
import { useRoute } from '@react-navigation/native';

import { Alert, DocumentBrowse, ImageUpload, isWeb, LEVELS } from '@helpers';
import { scaleOP } from '@components/frame';
import { fontOP } from '@styles/fonts';
import { waitForDocumentProcessingChain } from '@reducers/utils';
import { scheduledProductionOperationIdParam, OPERATOR_PANEL_INFORMATION_SCREENS } from '@components/operatorPanel/constants/operatorPanel.constants';
import { KEYS_TAGS } from '@components/imageHandling/common';
import { getUniqueNcFilesFromJobs } from './helpers';
import { IMAGE_UPLOAD_FOOTER_TEXT, IMAGE_UPLOAD_HEADER_TEXT, IMAGE_UPLOAD_MAIN_TEXT } from '@v2/pages/common/constants/base.constants';
import { trackEvent, TRACKING_EVENTS } from '@services/tracking';
import { useSelector } from 'react-redux';
import { selectSelectedItem } from '@reducers/machines';
import i18n from 'i18n-js';
import { NCFileSearchItem, OperationSearchItem, ProductionBatchStatistics } from '@interfaces/search';
import { ReducerEntityWithLoadingState } from '@v2/pages/common/interfaces';
import { ImageDocumentStoreItem } from '@v2/components/imageHandling/types/types';

// Simplified NC File type for our usage
export interface NCFileItem {
  ncFileId: number;
  ncFileName: string;
}
interface NCFileManagementRouteParams {
  [scheduledProductionOperationIdParam]?: string;
  ncFileId?: string;
  id?: string;
}

export interface UseNCFileManagementProps {
  jobs: ReducerEntityWithLoadingState<OperationSearchItem[]>;
  setJobs: (jobs: ReducerEntityWithLoadingState<OperationSearchItem[]>) => void;
  batch: ReducerEntityWithLoadingState<ProductionBatchStatistics | null>;
  incrementTabCount: (value: any) => any;
}

export interface UseNCFileManagementResult {
  currentNCFile: any;
  setCurrentNCFile: (file: any) => void;
  uploadingFiles: any[];
  toggleNCFileUpload: () => void;
  ncFiles: any[];
  ncFileListProps: {
    ncFiles: any[];
    ncFile: any;
    setNCFile: (file: any) => void;
    onAddNCFile: () => void;
    mainNcFileId: any;
    uploadingFiles: any[];
    noMain: boolean;
  };
  isMachinePerspective: boolean;
  currentNCFileLoaded: boolean;
}

export const useNCFileManagement = ({ jobs, batch, incrementTabCount, setJobs }: UseNCFileManagementProps): UseNCFileManagementResult => {
  const selectedMachine = useSelector(selectSelectedItem) as { machineId?: number; shortName?: string; fullName?: string };
  const [currentNCFile, setCurrentNCFile] = useState<any>(null);
  const [currentNCFileLoaded, setCurrentNCFileLoaded] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<any>([]);

  const route = useRoute();
  const routeParams = route?.params as NCFileManagementRouteParams | undefined;
  const selectedJobId = routeParams?.[scheduledProductionOperationIdParam];
  const ncFileIdFromParams = routeParams?.ncFileId;
  const isMachinePerspective = selectedJobId == null;
  const isToolsScreen = route.name === OPERATOR_PANEL_INFORMATION_SCREENS.Tools;

  const selectedJob = (!isMachinePerspective && selectedJobId ? jobs.data : batch.data?.links)?.find((j) => j.productionOperationId === +(selectedJobId ?? 0));

  // Memoize the ncFileList calculation to prevent infinite re-renders
  const ncFileList = useMemo(() => getUniqueNcFilesFromJobs(jobs), [jobs]);

  // Initialize current NC file based on perspective or route params
  useEffect(() => {
    if (ncFileIdFromParams) {
      const ncFileFromParams = ncFileList.find((f) => f.ncFileId === +ncFileIdFromParams);
      if (ncFileFromParams) {
        setCurrentNCFile({
          ncFileId: ncFileFromParams.ncFileId,
          ncFileName: ncFileFromParams.ncFileName,
        });
        setCurrentNCFileLoaded(true);
        return;
      }
    }

    if (isMachinePerspective) {
      if (batch?.data) {
        setCurrentNCFile({
          ncFileId: batch.data.ncFileId,
          ncFileName: batch.data.ncFileName,
        });
        setCurrentNCFileLoaded(true);
      }
    } else {
      if (ncFileList.length) {
        if (isToolsScreen) {
          setCurrentNCFile('all');
        } else {
          setCurrentNCFile({
            ncFileId: ncFileList[0].ncFileId,
            ncFileName: ncFileList[0].ncFileName,
          });
        }
      }
      setCurrentNCFileLoaded(true);
    }
  }, [batch?.data, isMachinePerspective, ncFileList, ncFileIdFromParams]);

  const onSuccessAddNcFileToJob = (ncFile: NCFileSearchItem | ImageDocumentStoreItem) => {
    const isNcFile = (file: NCFileSearchItem | ImageDocumentStoreItem): file is NCFileSearchItem => file?.ncFileId != null;
    if (!isNcFile(ncFile)) return;

    const selectedNcFile = {
      changeTimePerPcsSecond: ncFile.changeTimePerPcsSecond,
      createdOn: ncFile.createdOn,
      linkedBy: ncFile.linkedBy,
      linkedOn: ncFile.linkedOn,
      memo: ncFile.memo,
      ncFileId: ncFile.ncFileId,
      ncFileName: ncFile.ncFileName,
      ncFileStatus: ncFile.ncFileStatus,
      runTimePerPcsSecond: ncFile.runTimePerPcsSecond,
      setupSheet: ncFile.setupSheet,
      setupTimeSecond: ncFile.setupTimeSecond,
      productionBatchId: isMachinePerspective ? batch.data?.productionBatchId : null,
    };

    if (ncFileList.every((ncFile) => ncFile.ncFileId !== selectedNcFile.ncFileId)) {
      setJobs({
        data: jobs.data?.map((j) => ({
          ...j,
          linkedNcFiles: j.linkedNcFiles ? [...new Set([selectedNcFile, ...j.linkedNcFiles])] : [selectedNcFile],
        })),
        wasLoaded: true,
      });
      incrementTabCount({tabName: OPERATOR_PANEL_INFORMATION_SCREENS.NcFiles}); 
    } else {
      Alert({ type: 'danger', title: 'NC File already added' } as any);
    }
  };

  const toggleNCFileUpload = useCallback(() => {
    const contextId = batch?.data?.links?.[0]?.productionOperationId || selectedJobId;

    if (!contextId) {
      return Alert({ type: 'danger', title: 'Job not found' } as any);
    }

    ImageUpload({
      addingNcFile: true,
      noPhoto: true,
      addExistingSearchLevel: LEVELS.NCFiles,
      onSuccessAddingExisting: onSuccessAddNcFileToJob,
      uploadStart: async (data: any) => {
        const randomId = Math.round(Math.random() * 10000);
        const fileBase = {
          ncFileName: data?.name,
          ncFileId: randomId,
          status: 'uploading',
        };
        setUploadingFiles((prev: any) => [...prev, fileBase]);

        if (!currentNCFile) {
          setCurrentNCFile(fileBase);
        }

        try {
          const result = await waitForDocumentProcessingChain({
            fileName: data?.name,
            productionOperationId: contextId,
          } as any);

          setUploadingFiles((prev: any) =>
            prev.map((i: any) =>
              i.ncFileId == randomId
                ? {
                    ncFileName: data?.name,
                    ncFileId: result.correlationData.ncFileId,
                    status: 'done',
                  }
                : i
            )
          );
          if (!currentNCFile || currentNCFile?.ncFileId == randomId) {
            setCurrentNCFile({
              ncFileName: data?.name,
              ncFileId: result.correlationData.ncFileId,
            });
          }
          incrementTabCount({ tabName: OPERATOR_PANEL_INFORMATION_SCREENS.NcFiles });
        } catch (e) {
          setUploadingFiles((prev: any) =>
            prev.map((i: any) =>
              i.ncFileId == randomId
                ? {
                    ncFileName: data?.name,
                    ncFileId: randomId,
                    status: 'error',
                  }
                : i
            )
          );
        }
      },
      documentType: 'NcFile',
      tags: isMachinePerspective
        ? (batch?.data?.links ?? []).map((i: any) => {
            const job = jobs.data?.find((j) => j.productionOperationId === i.productionOperationId);
            return {
              scopeId: KEYS_TAGS.ProductionOperation,
              scopeReferenceId: String(i.productionOperationId),
              scopeReferenceName: job ? `${job.productionOrderNumber}-${job.productionOperationNumber}` : '',
            };
          })
        : [
            {
              scopeId: KEYS_TAGS.ProductionOperation,
              scopeReferenceId: String(selectedJobId),
              scopeReferenceName: selectedJob ? `${selectedJob.productionOrderNumber}-${selectedJob.productionOperationNumber}` : '',
            },
          ],
      headerText: IMAGE_UPLOAD_HEADER_TEXT.AddNcFile,
      footerText: IMAGE_UPLOAD_FOOTER_TEXT.AddNcFile,
      mainText: IMAGE_UPLOAD_MAIN_TEXT.NcFile,
      ...(selectedJob
        ? {
            headerDescription: i18n.t('To Job {{operationNumber}}', {
              operationNumber: isMachinePerspective
                ? `${selectedJob.productionOrderId}-${selectedJob.productionOperationId}`
                : selectedJob.productionOperationId,
            }),
          }
        : {}),
      scale: scaleOP,
      font: fontOP,
    });
  }, [batch, selectedJobId, isMachinePerspective, incrementTabCount, jobs, selectedJob]);

  // Prepare NC files list based on perspective
  const preparedNCFiles = isMachinePerspective
    ? [
        // Only include batch file if it has valid data
        ...(batch?.data?.ncFileId && batch?.data?.ncFileName
          ? [
              {
                ncFileId: batch.data.ncFileId,
                ncFileName: batch.data.ncFileName,
              },
            ]
          : []),
        ...ncFileList,
      ]
    : ncFileList;

  const handleNCFileSelect = useCallback((ncFile: NCFileItem) => {
    trackEvent(TRACKING_EVENTS.OPERATOR_PANEL, {
      action: 'ncFileSelected',
      machineId: String(selectedMachine?.machineId || ''),
      machineName: selectedMachine?.shortName || selectedMachine?.fullName || '',
    });
    setCurrentNCFile(ncFile);
  }, [selectedMachine]);

  const ncFileListProps = {
    ncFiles: preparedNCFiles,
    ncFile: currentNCFile,
    setNCFile: handleNCFileSelect,
    onAddNCFile: toggleNCFileUpload,
    mainNcFileId: batch?.data?.ncFileId,
    uploadingFiles,
    noMain: !isMachinePerspective,
  };

  return {
    currentNCFile,
    setCurrentNCFile,
    uploadingFiles,
    toggleNCFileUpload,
    ncFileListProps,
    isMachinePerspective,
    currentNCFileLoaded,
    ncFiles: preparedNCFiles,
  };
};
